import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { Box, Link, Icon } from '@nimbus-ds/components';
import { CheckCircleIcon, ChevronLeftIcon } from '@nimbus-ds/icons';
import { SideModal } from '@nimbus-ds/patterns';
import { useNavegate } from 'App/hooks';
import { useToastStatus } from 'commons/hooks';
import { catalogRoutes } from 'domains/Catalog';
import {
  SelectedProductInterface,
  OnChangeSelectType,
} from 'domains/Catalog/Products/components';
import useSelectProductsList from 'domains/Catalog/Products/components/SelectProductList/useSelectProductsList/useSelectProductsList';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import { SelectProductModalHeader } from './SelectProductModalHeader';
import { SelectProductModalList } from './SelectProductModalList';

interface SelectProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onChange: OnChangeSelectType;
  selectedProducts: SelectedProductInterface[];
  refreshProducts?: boolean;
  setRefreshProducts?: (refresh: boolean) => void;
  excludedProductIds?: string[];
}

export function SelectProductModal({
  isOpen,
  onClose,
  onChange,
  selectedProducts,
  refreshProducts,
  setRefreshProducts,
  excludedProductIds = [],
}: SelectProductModalProps): JSX.Element {
  const t = useTranslationCatalog();
  const internalServerError = t('subscriptions.form.products.searchError');
  const { goTo } = useNavegate();
  const [searchTerm, setSearchTerm] = useState('');
  const {
    products,
    isLoading,
    isInternalServerError,
    isSuccess,
    fetchDraftOrderProducts,
    getMoreDraftOrdersProducts,
    isRefreshing,
  } = useSelectProductsList({
    sortBy: null,
  });

  const handleLoadMore = useCallback(() => {
    if (!isLoading && !isRefreshing) {
      getMoreDraftOrdersProducts(searchTerm);
    }
  }, [isLoading, isRefreshing, getMoreDraftOrdersProducts, searchTerm]);

  useToastStatus({
    error: internalServerError,
    status: isInternalServerError ? 'error' : 'idle',
  });

  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      fetchDraftOrderProducts(searchTerm);
    }, 300);
    setRefreshProducts?.(false);
    return () => clearTimeout(debouncedSearch);
  }, [
    searchTerm,
    fetchDraftOrderProducts,
    refreshProducts,
    setRefreshProducts,
  ]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value),
    [],
  );

  const handleOnClearSelecteds = useCallback(() => onChange([]), [onChange]);
  const handleSearch = useCallback(
    () => fetchDraftOrderProducts(searchTerm),
    [fetchDraftOrderProducts, searchTerm],
  );

  const handleOnSelect = useCallback(
    (productId: string, checked: boolean) => {
      if (checked) {
        const product = products?.filter((product) => product.id === productId);
        onChange([...selectedProducts, ...(product || [])]);
      } else {
        onChange(
          selectedProducts.filter((product) => product.id !== productId),
        );
      }
    },
    [products, selectedProducts, onChange],
  );

  const goToCreateProductPage = useCallback(() => {
    onClose();
    goTo(catalogRoutes.newProduct);
  }, [onClose, goTo]);

  return (
    <SideModal
      open={isOpen}
      onRemove={onClose}
      maxWidth={{ xs: '100%', md: '45%', lg: '540px' }}
      paddingBody="none"
      title=""
      headerAction={
        <Link as="button" onClick={onClose} textDecoration="none">
          <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
        </Link>
      }
      headerIcon={
        <Link appearance="primary" as="button" onClick={onClose}>
          <Icon color="neutral-textHigh" source={<CheckCircleIcon />} />
        </Link>
      }
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="stretch"
        height="calc(100vh - 85px)"
        gap="3"
      >
        {!(isSuccess && (!products || products.length === 0)) && (
          <SelectProductModalHeader
            searchValue={searchTerm}
            selectedCount={selectedProducts.length}
            excludedProductsCount={excludedProductIds.length}
            products={products}
            onSearchChange={handleOnChange}
            onSearch={handleSearch}
            onClearSelection={handleOnClearSelecteds}
          />
        )}
        <SelectProductModalList
          isLoading={isLoading}
          isRefreshing={isRefreshing}
          isSuccess={isSuccess}
          searchValue={searchTerm}
          products={products}
          selectedProducts={selectedProducts}
          excludedProductIds={excludedProductIds}
          onLoadMore={handleLoadMore}
          onSelect={handleOnSelect}
          onCreateProduct={goToCreateProductPage}
        />
      </Box>
    </SideModal>
  );
}
